import {
  CSSProperties,
  ReactNode,
  useRef,
  useEffect,
  useCallback,
  SyntheticEvent,
} from "react";
import { Resizable } from "react-resizable";
import { ResizeCallbackData } from "react-resizable";
import "./Table.module.css";

export interface ResizableColumnProps {
  width: number;
  colIndex?: string;
  widths: { [key: string]: number };
  setWidth: (colIndex: string, width: number) => void;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}

export function ResizableColumn({
  width,
  colIndex,
  widths,
  setWidth,
  children,
  className,
  style,
}: ResizableColumnProps) {
  // Track resize state
  const isResizing = useRef(false);
  const startWidthRef = useRef<number>(0);
  const startXRef = useRef<number>(0);
  const widthsRef = useRef(widths);

  // Keep widthsRef in sync
  useEffect(() => {
    widthsRef.current = widths;
  }, [widths]);

  const handleResize = useCallback(
    (e: SyntheticEvent, { size }: ResizeCallbackData) => {
      e.stopPropagation();
      if (!colIndex || !isResizing.current) return;

      const mouseEvent = e as unknown as MouseEvent;
      const currentX = mouseEvent.clientX;
      const deltaX = currentX - startXRef.current;
      const newWidth = Math.max(startWidthRef.current + deltaX, 50);

      // Update width in state
      setWidth(colIndex, newWidth);

      // Update DOM immediately
      const styleText = `
      width: ${newWidth}px !important;
      min-width: ${newWidth}px !important;
      flex: 0 0 ${newWidth}px !important;
    `;

      document
        .querySelectorAll(`[data-col-index="${colIndex}"]`)
        .forEach((el) => {
          (el as HTMLElement).style.cssText = styleText;
        });
    },
    [colIndex, setWidth]
  );

  const handleResizeStart = useCallback(
    (e: SyntheticEvent) => {
      if (!colIndex) return;

      const mouseEvent = e as unknown as MouseEvent;

      // Get the actual current width from the DOM element
      const element = document.querySelector(
        `[data-col-index="${colIndex}"]`
      ) as HTMLElement;
      const currentWidth = element
        ? element.getBoundingClientRect().width
        : widths[colIndex] || width;

      startWidthRef.current = currentWidth;
      startXRef.current = mouseEvent.clientX;
      isResizing.current = true;

      console.log("Resize START:", {
        colIndex,
        startWidth: currentWidth,
        currentWidths: widths,
        domWidth: element?.getBoundingClientRect().width,
        timestamp: new Date().toISOString(),
      });
    },
    [colIndex, widths, width]
  );

  const handleResizeStop = useCallback(
    (e: SyntheticEvent) => {
      if (!colIndex) return;

      console.log("Resize STOP:", {
        colIndex,
        finalWidth: widths[colIndex],
        currentWidths: widths,
        timestamp: new Date().toISOString(),
      });

      isResizing.current = false;
      startWidthRef.current = 0;
      startXRef.current = 0;
    },
    [colIndex, widths]
  );

  return (
    <Resizable
      width={width}
      height={0}
      onResize={handleResize}
      onResizeStart={handleResizeStart}
      onResizeStop={handleResizeStop}
      minConstraints={[50, 0]}
      maxConstraints={[1000, 0]}
      draggableOpts={{
        enableUserSelectHack: false,
        grid: [1, 1],
      }}
      className="debug-resizable"
    >
      <th
        data-col-index={colIndex}
        className={`${className} resizable-column debug-resizable-th`}
        style={{
          ...style,
          width: `${width}px`,
          minWidth: `${width}px`,
          flex: `0 0 ${width}px`,
          position: "relative",
          willChange: "width",
          transform: "translateZ(0)",
        }}
      >
        {children}
      </th>
    </Resizable>
  );
}
